import { create } from 'jsondiffpatch';
import { partition } from 'lodash';
import { getAggregatedOrderGrouping } from '../../aggregated-order-panel/group-popover/useGroupPopover';
import {
  getFilterState,
  transformOrderEntry,
  transFormAndFlattenOrderEntries,
  getValidGrouping,
  getTableData,
  ExtendedOrder,
} from '../../aggregated-order-panel/aggregated-order-table/useAggregatedOrderTable';
import { columnNameToColumnId } from '../../aggregated-order-panel/utils';
import { ResolvedOrder } from 'src/hooks/order';
import { OrderDeepFragment } from 'src/gql/graphql';

function processEntries(
  shouldFlatten: boolean,
  resolvedOrders: ResolvedOrder[],
  thisOrder: string
) {
  if (shouldFlatten) {
    return resolvedOrders.flatMap((order: ResolvedOrder) =>
      transFormAndFlattenOrderEntries(order, thisOrder)
    );
  }
  return resolvedOrders.flatMap((order: ResolvedOrder) =>
    order.orderEntries.map((orderEntry) =>
      transformOrderEntry(orderEntry, order, thisOrder)
    )
  );
}

export function getAggregatedOrderDiffingTableRows(
  aggOrder: ExtendedOrder,
  orders: ResolvedOrder[]
) {
  const columnFilters = getFilterState(
    aggOrder.aggregatedOrderFilters,
    aggOrder.orderColumns
  );
  const grouping = getAggregatedOrderGrouping(
    aggOrder as unknown as OrderDeepFragment
  );
  const validGrouping = getValidGrouping(grouping, aggOrder.orderColumns);
  const data = processEntries(
    Boolean(aggOrder.areItemsFlattened),
    orders,
    aggOrder.id
  );

  const aggArray = aggOrder.aggregatedOrderAggregation?.map(
    (agg: any) =>
      [
        columnNameToColumnId(
          agg.columnName!,
          aggOrder.orderColumns,
          agg.columnType
        ),
        agg.aggregationValue,
      ] as [string, string]
  );
  const aggregation = aggArray ? Object.fromEntries(aggArray) : {};
  return getTableData({
    data,
    grouping: validGrouping,
    columnFilters,
    aggregation,
    orderColumns: aggOrder.orderColumns,
  });
}

// Function to patch the aggregated orders for diffing
// hopefully it will be sync
function patchAggregatedOrders(stateData: any) {
  // if we have a structure that doesn't look like the state we just return the statedata without mods
  // This will patch the issues we are experiencing with testing
  if (!stateData?.project?.orders) return stateData;
  // Grab all the aggregated orders to iterate over
  // Grab all the normal orders and grab entries from them
  const allOrders = stateData.project.orders;
  const [aggregatedOrders, orders] = partition(
    allOrders,
    (order: any) => order.isAggregatedOrder
  );
  for (const aggOrder of aggregatedOrders) {
    const rows = getAggregatedOrderDiffingTableRows(aggOrder, orders);
    aggOrder['orderEntries'] = rows;
  }
  // hopefuly the state data has been modified at this point
  return stateData;
}

// eslint-disable-next-line complexity
export function normalizeJsonForDiff(input: any, inSubrow = false): any {
  // Primitives and null
  if (input === null || typeof input !== 'object') return input;

  // Arrays
  if (Array.isArray(input)) {
    // Handle array of primitives
    if (input.every((v) => v === null || typeof v !== 'object')) {
      // return [...input].sort();
      return JSON.stringify(input);
    }
    // transform the array into a map if the elements have a stable identity
    // This array contains the potential stable keys, order is important
    const idKeys = [
      ['id'],
      // ['name'],
      ['columnName'],
      ['modelUrn'],
      ['predicate'],
      ['sheetId', 'pageNumber'],
    ];

    if (inSubrow) {
      idKeys.unshift(['name']);
    }

    let chosenKeys: string[] = [];
    for (const keys of idKeys) {
      if (input.every((item) => keys.every((key) => key in item))) {
        chosenKeys = keys;
        // These keys are ordered in precedence, if we find a set of keys early on, we do not continue the search
        break;
      }
    }

    if (chosenKeys.length > 0) {
      const mapFromArr: Record<string, any> = {};
      for (const item of input) {
        const key = chosenKeys.map((key) => item[key]).join('-');
        mapFromArr[key] = normalizeJsonForDiff(item);
      }
      return mapFromArr;
    }

    // For arrays of non-primitives without stable identities, we'll treat them as atomic units
    // by converting them to a string representation
    return JSON.stringify(input.map((item) => normalizeJsonForDiff(item)));
  }
  const normalized: Record<string, any> = {};
  for (const key of Object.keys(input).sort()) {
    // if we are in a grouped subrow, favour the id instead of name as key
    const issr = key === 'subRows' && !input['__isGrouped'];
    normalized[key] = normalizeJsonForDiff(input[key], issr);
  }
  return normalized;
}

// Flatten diff into an map of individual diffs keyed by path
// eslint-disable-next-line complexity
export function flattenDiff(
  delta: any,
  path: Array<string> = [],
  out: Record<string, any> = {}
) {
  if (!delta) return out;

  // Handle the case where delta is a direct value change
  if (
    Array.isArray(delta) &&
    delta.length === 2 &&
    (typeof delta[0] !== 'object' ||
      delta[0] === null ||
      // NEW: allow object replacement unless it looks like jsondiffpatch metadata
      (typeof delta[0] === 'object' && !('t' in delta[0])))
  ) {
    const pathStr = path.join('.') || '_top_level';
    out[pathStr] = { old: delta[0], new: delta[1] };
    return out;
  }

  for (const key in delta) {
    if (key === 't') continue; // skip jsondiffpatch array marker
    const val = delta[key];
    const fullPath = [...path, key];

    if (
      Array.isArray(val) &&
      val.length === 2 &&
      (typeof val[0] !== 'object' ||
        val[0] === null ||
        (typeof val[0] === 'object' && !('t' in val[0])))
    ) {
      // Regular value or whole-object replacement
      out[fullPath.join('.')] = { old: val[0], new: val[1] };
    } else if (typeof val === 'object' && !Array.isArray(val)) {
      // Dive deeper into nested diffs
      flattenDiff(val, fullPath, out);
    } else if (Array.isArray(val)) {
      if (val.length === 3 && val[1] === 0 && val[2] === 0) {
        // Removed
        out[fullPath.join('.')] = { old: val[0], new: undefined };
      } else if (val.length === 1) {
        // Added
        out[fullPath.join('.')] = { old: undefined, new: val[0] };
      }
    }
  }
  return out;
}

export function generateDiff(resolvedStates: [any, any]) {
  const [base, secondary] = resolvedStates.map((item) =>
    normalizeJsonForDiff(patchAggregatedOrders(item))
  );

  // Ensure that only same objects are diffed.
  const diffPatcher = create({
    // eslint-disable-next-line complexity
    objectHash: function (obj: any) {
      // fallback default: generates a unique hash for object identity detection
      if (obj === null || obj === undefined) return JSON.stringify(obj);
      if (typeof obj === 'number' && Number.isNaN(obj)) {
        return 'NaN';
      }
      if (typeof obj !== 'object') return String(obj);
      // you can stable stringify and hash whatever that remains
      return JSON.stringify(obj);
    },
    // We are not interested in diffs that contain dates
    propertyFilter: function (name, context) {
      return ![
        'createdAt',
        'updatedAt',
        'color',
        'elementIdentifiersDbid',
        'elementIdentifiersQuery',
        'usePromptForLinking',
        'promptForLinking',
      ].includes(name);
    },
    arrays: {
      detectMove: false,
      includeValueOnMove: false,
    },
  });
  const rawDiff = diffPatcher.diff(base, secondary);
  console.log('the raw diff: ', rawDiff);
  return flattenDiff(rawDiff);
}
